"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export enum PortalName {
  pricing_modal = "pricing-modal",
  subscription_upgrade_modal = "subscription-upgrade-modal",
  usage_limit_modal = "usage-limit-modal",
  anonymous_signup_modal = "anonymous-signup-modal",
  /** Add more portal names as the app grows */
}

export type AnonymousSignupModalReason = "after_message" | "after_checkout";

export type AnonymousSignupModalPortalData = {
  reason?: AnonymousSignupModalReason;
};

type PortalState = {
  open: boolean;
  data?: unknown;
};

type PortalsState = Partial<Record<string, PortalState>>;

type PortalAction =
  | { type: "OPEN"; name: string; data?: unknown }
  | { type: "CLOSE"; name: string }
  | { type: "CLOSE_ALL"; except?: string[] }
  | { type: "DESTROY"; name: string };

// Optional: type-safe data per portal (extend as needed)
export type PortalDataType = {
  [PortalName.pricing_modal]: undefined;
  [PortalName.subscription_upgrade_modal]: undefined;
  [PortalName.usage_limit_modal]: undefined;
  [PortalName.anonymous_signup_modal]: AnonymousSignupModalPortalData | undefined;
  [key: string]: unknown;
};

export interface PortalsContextValue {
  portals: PortalsState;
  open: <T>(name: string, data?: T) => void;
  openPortal: <T extends PortalName>(name: T, data?: PortalDataType[T]) => void;
  close: (name: string) => void;
  togglePortal: <T>(name: string, data?: T) => void;
  isOpen: (name: string) => boolean;
  getPortalData: <T extends PortalName>(name: T) => PortalDataType[T];
  closeAll: (except?: string[]) => void;
  destroy: (name: string) => void;
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

function portalsReducer(state: PortalsState, action: PortalAction): PortalsState {
  switch (action.type) {
    case "OPEN":
      return {
        ...state,
        [action.name]: { open: true, data: action.data },
      };
    case "CLOSE":
      return {
        ...state,
        [action.name]: { ...state[action.name], open: false },
      };
    case "CLOSE_ALL": {
      const except = new Set(action.except ?? []);
      return Object.fromEntries(
        Object.entries(state).map(([key, value]) => [
          key,
          except.has(key) ? value : { ...value, open: false },
        ]),
      );
    }
    case "DESTROY": {
      const next = { ...state };
      delete next[action.name];
      return next;
    }
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const PortalsContext = createContext<PortalsContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function PortalContextProvider({ children }: { children: ReactNode }) {
  const [portals, dispatch] = useReducer(portalsReducer, {});

  const open = useCallback(<T,>(name: string, data?: T) => {
    dispatch({ type: "OPEN", name, data });
  }, []);

  const openPortal = useCallback(
    <T extends PortalName>(name: T, data?: PortalDataType[T]) => {
      dispatch({ type: "OPEN", name, data });
    },
    [],
  );

  const close = useCallback((name: string) => {
    dispatch({ type: "CLOSE", name });
  }, []);

  const isOpen = useCallback(
    (name: string) => Boolean(portals[name]?.open),
    [portals],
  );

  const getPortalData = useCallback(
    <T extends PortalName>(name: T): PortalDataType[T] => {
      const raw = portals[name]?.data;
      return (raw ?? undefined) as PortalDataType[T];
    },
    [portals],
  );

  const togglePortal = useCallback(
    <T,>(name: string, data?: T) => {
      if (portals[name]?.open) {
        dispatch({ type: "CLOSE", name });
      } else {
        dispatch({ type: "OPEN", name, data });
      }
    },
    [portals],
  );

  const closeAll = useCallback((except?: string[]) => {
    dispatch({ type: "CLOSE_ALL", except });
  }, []);

  const destroy = useCallback((name: string) => {
    dispatch({ type: "DESTROY", name });
  }, []);

  const value = useMemo<PortalsContextValue>(
    () => ({
      portals,
      open,
      openPortal,
      close,
      togglePortal,
      isOpen,
      getPortalData,
      closeAll,
      destroy,
    }),
    [
      portals,
      open,
      openPortal,
      close,
      togglePortal,
      isOpen,
      getPortalData,
      closeAll,
      destroy,
    ],
  );

  return (
    <PortalsContext.Provider value={value}>{children}</PortalsContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function usePortals(): PortalsContextValue {
  const value = useContext(PortalsContext);
  if (value == null) {
    throw new Error("usePortals must be used within PortalContextProvider");
  }
  return value;
}

// ---------------------------------------------------------------------------
// HOC: withRenderPortal
// ---------------------------------------------------------------------------

export interface WithRenderPortalProps {
  name: string;
  data?: unknown;
  open: boolean;
  onClose: () => void;
}

export type WrappedComponentProps<T> = {
  forceRender?: boolean;
  blurActiveElement?: boolean;
  name?: string;
} & Omit<T, keyof WithRenderPortalProps>;

/**
 * HOC that manages portal rendering lifecycle.
 * Handles delayed unmounting for exit animations (300ms).
 */
export function withRenderPortal<T extends WithRenderPortalProps>(name: string) {
  return function withRenderPortalWrapper(
    WrappedComponent: ComponentType<T>,
  ): ComponentType<WrappedComponentProps<T>> {
    function PortalWrapper(props: WrappedComponentProps<T>) {
      const portalName = (props?.name ?? name) as PortalName;
      const { isOpen, close, getPortalData, destroy } = usePortals();

      const [shouldRender, setShouldRender] = useState(() => isOpen(portalName));
      const isOpenRef = useRef(isOpen(portalName));

      const open = isOpen(portalName);

      useEffect(() => {
        isOpenRef.current = open;
      }, [open]);

      const handleClose = useCallback(() => {
        isOpenRef.current = false;
        close(portalName);
      }, [close, portalName]);

      useEffect(() => {
        if (open) {
          if (props.blurActiveElement) {
            (document.activeElement as HTMLElement | null)?.blur?.();
          }
          const openId = setTimeout(() => setShouldRender(true), 0);
          return () => clearTimeout(openId);
        }

        const id = setTimeout(() => setShouldRender(open), 300);
        return () => clearTimeout(id);
      }, [open, props.blurActiveElement]);

      useEffect(() => {
        return () => {
          if (shouldRender && !isOpenRef.current) {
            destroy(portalName);
          }
        };
      }, [shouldRender, destroy, portalName]);

      if (!shouldRender && !props.forceRender) {
        return null;
      }

      return (
        <WrappedComponent
          {...(props as T)}
          open={isOpen(portalName)}
          name={portalName}
          onClose={handleClose}
          data={getPortalData(portalName)}
        />
      );
    }

    PortalWrapper.displayName = `WithRenderPortal(${name})`;
    return PortalWrapper;
  };
}
